import env from 'src/env';
import type {
  RegistrationsResponse,
  WorkflowRequest,
  WorkflowResponse,
} from 'src/types';
import { makeRequest } from '../apiService';

export const getRegistrations = async (
  ncn?: string,
  status?: string,
  page?: number,
  pageSize?: number
): Promise<RegistrationsResponse> => {
  const params: any = {};

  if (ncn) params.ncn = ncn;

  if (status) {
    params.status = status;
  }

  if (page !== undefined) {
    params.page = page;
  }

  if (pageSize !== undefined) {
    params.pageSize = pageSize;
  }

  const { data } = await makeRequest<{
    data: RegistrationsResponse;
    pagination: any;
  }>({
    method: 'GET',
    url: `${env.ARLO_BASE_URL}/registrations`,
    params,
  });

  return { ...data.data, pagination: data.pagination };
};

export const approveRegistration = async (
  registrationId: string
): Promise<WorkflowResponse> => {
  const requestBody: WorkflowRequest = {
    status: 'Approved',
  };

  const { data } = await makeRequest<
    { data: WorkflowResponse },
    WorkflowRequest
  >(
    {
      method: 'POST',
      url: `${env.ARLO_BASE_URL}/registrations/${registrationId}/approve`,
    },
    requestBody
  );

  return data.data;
};

export const rejectRegistration = async (
  registrationId: string
): Promise<WorkflowResponse> => {
  const requestBody: WorkflowRequest = {
    status: 'Rejected',
  };

  const { data } = await makeRequest<
    { data: WorkflowResponse },
    WorkflowRequest
  >(
    {
      method: 'POST',
      url: `${env.ARLO_BASE_URL}/registrations/${registrationId}/reject`,
    },
    requestBody
  );

  return data.data;
};

const readBlobText = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });

export const exportRegistrations = async (ncn: string): Promise<Blob> => {
  const { data } = await makeRequest<Blob>({
    method: 'GET',
    url: `${env.ARLO_BASE_URL}/registrations/export`,
    params: { ncn },
    responseType: 'blob',
  });

  if (data instanceof Blob) {
    if (data.type.includes('json')) {
      const text = await readBlobText(data);
      let message = 'Failed to export registrations';
      try {
        const parsed = JSON.parse(text) as { message?: string };
        if (parsed.message) message = parsed.message;
      } catch {
        // use default message
      }
      throw new Error(message);
    }
    return data;
  }

  if (typeof data === 'string') {
    return new Blob([data], { type: 'text/csv;charset=utf-8' });
  }

  throw new Error('Unexpected export response format');
};
